import { resolveWindows, type OperatingCalendar } from "@opus-dt/shared-types";
import { blockRangeGeometryPct, clamp, type TimelineRange } from "./timeline-range";
import type { ProductionOrder } from "../../types/production-orders.types.js";

// Faixas "fora do expediente" no gantt: o COMPLEMENTO das janelas operacionais da
// linha dentro da faixa visível, em % (recortadas às bordas). Calendário null (24/7) ⇒ sem faixas.
// Pura (testada): o timezone é injetado (componentes usam o do navegador — convenção local da
// timeline; testes passam "UTC"). Uma linha com calendário mas sem turnos ⇒ faixa cobre tudo.
export function outOfWindowBandsPct(
  calendar: OperatingCalendar | null | undefined,
  range: TimelineRange,
  timezone: string
): { leftPct: number; widthPct: number }[] {
  if (!calendar) return [];
  const span = range.endMs - range.startMs;
  if (span <= 0) return [];
  const { windows } = resolveWindows(
    calendar,
    new Date(range.startMs),
    new Date(range.endMs),
    timezone
  );
  const pct = (s: number, e: number) => ({
    leftPct: ((s - range.startMs) / span) * 100,
    widthPct: ((e - s) / span) * 100,
  });
  const bands: { leftPct: number; widthPct: number }[] = [];
  let cursor = range.startMs;
  for (const [s, e] of windows) {
    if (s > cursor) bands.push(pct(cursor, s));
    cursor = Math.max(cursor, e);
  }
  if (cursor < range.endMs) bands.push(pct(cursor, range.endMs));
  return bands;
}

// Geometria do mini-gantt de 1 dia (eixo 00:00–24:00 local). A timeline principal
// (Dia/Semana/Mês) usa `timeline-range.ts`; conflitos + agrupamento por linha são compartilhados
// pelas duas. Funções puras p/ teste. `clamp` vem de timeline-range (fonte única, D-5).
const DAY_START_HOUR = 0;
const DAY_END_HOUR = 24;

function dayBounds(dayIso: string): { start: number; end: number } {
  // dayIso "yyyy-mm-dd" + "T00:00:00" (sem Z) → meia-noite LOCAL (não UTC), senão em fuso negativo
  // `new Date("yyyy-mm-dd")` cairia no dia anterior local.
  const start = new Date(`${dayIso}T00:00:00`);
  start.setHours(DAY_START_HOUR, 0, 0, 0);
  const end = new Date(`${dayIso}T00:00:00`);
  end.setHours(DAY_END_HOUR, 0, 0, 0);
  return { start: start.getTime(), end: end.getTime() };
}

// left/width em % do eixo do dia (00–24h), recortado às bordas (OP que extrapola o dia é clampada).
export function blockGeometry(
  startIso: string,
  endIso: string,
  dayIso: string
): { leftPct: number; widthPct: number } {
  const { start, end } = dayBounds(dayIso);
  const span = end - start;
  const s = clamp(new Date(startIso).getTime(), start, end);
  const e = clamp(new Date(endIso).getTime(), start, end);
  const leftPct = ((s - start) / span) * 100;
  const widthPct = Math.max(((e - s) / span) * 100, 1);
  return { leftPct, widthPct };
}

// Conflito = sobreposição semiaberta entre OPs SCHEDULED/RUNNING na MESMA linha (dados legados).
export function detectConflicts(
  orders: Pick<ProductionOrder, "id" | "lineId" | "plannedStartAt" | "expectedEnd" | "status">[]
): Set<string> {
  const active = orders.filter((o) => o.status === "SCHEDULED" || o.status === "RUNNING");
  const conflicts = new Set<string>();
  for (let i = 0; i < active.length; i++) {
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i]!;
      const b = active[j]!;
      if (a.lineId !== b.lineId) continue;
      const as = new Date(a.plannedStartAt).getTime();
      const ae = new Date(a.expectedEnd).getTime();
      const bs = new Date(b.plannedStartAt).getTime();
      const be = new Date(b.expectedEnd).getTime();
      if (as < be && bs < ae) {
        conflicts.add(a.id);
        conflicts.add(b.id);
      }
    }
  }
  return conflicts;
}

// Overrun: OP em execução (RUNNING) cujo fim previsto já passou e segue ativa. A correção
// é de LEITURA (nunca mutar expectedEnd): a barra estica até "agora", marcando o previsto com um tick
// e destacando o trecho de estouro. Funções puras (sem DOM) — testadas em geometry.test.ts.

export function isOverrun(o: Pick<ProductionOrder, "status" | "expectedEnd">, now: Date): boolean {
  return o.status === "RUNNING" && new Date(o.expectedEnd).getTime() < now.getTime();
}

export interface OverrunGeometry {
  // Barra esticada [plannedStartAt → max(expectedEnd, now)], recortada à faixa (null se não intersecta).
  bar: { leftPct: number; widthPct: number } | null;
  // % do expectedEnd planejado na faixa (tick), ou null quando cai fora da faixa visível.
  plannedEndPct: number | null;
  // Trecho de estouro [expectedEnd → now] recortado à faixa; null quando não há overrun visível.
  overrun: { leftPct: number; widthPct: number } | null;
}

// Geometria da barra em overrun. `effectiveEnd = max(expectedEnd, now)` estica até agora. Não
// muta nada: deriva tudo de plannedStartAt/expectedEnd + relógio. Pressupõe `isOverrun` verdadeiro.
export function overrunGeometry(
  plannedStartAt: string,
  expectedEnd: string,
  now: Date,
  range: TimelineRange
): OverrunGeometry {
  const expectedMs = new Date(expectedEnd).getTime();
  const effectiveEndIso = new Date(Math.max(expectedMs, now.getTime())).toISOString();
  const bar = blockRangeGeometryPct(plannedStartAt, effectiveEndIso, range);
  const span = range.endMs - range.startMs;
  const plannedEndPct =
    expectedMs < range.startMs || expectedMs > range.endMs
      ? null
      : ((expectedMs - range.startMs) / span) * 100;
  const overrun = blockRangeGeometryPct(expectedEnd, now.toISOString(), range);
  return { bar, plannedEndPct, overrun };
}

// Magnitude do atraso (now − expectedEnd) em rótulo curto: "+Xd Yh" / "+Yh Zm" / "+Zm". "" se ≤ 0.
export function overrunLabel(expectedEnd: string, now: Date): string {
  const ms = now.getTime() - new Date(expectedEnd).getTime();
  if (ms <= 0) return "";
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const min = totalMin % 60;
  if (days > 0) return `+${days}d ${hours}h`;
  if (hours > 0) return `+${hours}h ${String(min).padStart(2, "0")}m`;
  return `+${min}m`;
}

// Agrupa OPs por linha preservando a ordem de primeira aparição (uma swimlane por linha).
export function groupByLine(
  items: ProductionOrder[]
): { lineId: string; name: string; orders: ProductionOrder[] }[] {
  const m = new Map<string, { lineId: string; name: string; orders: ProductionOrder[] }>();
  for (const o of items) {
    const g = m.get(o.lineId) ?? { lineId: o.lineId, name: o.lineName, orders: [] };
    g.orders.push(o);
    m.set(o.lineId, g);
  }
  return [...m.values()];
}
